import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2 } from "lucide-react";
import { toast } from "sonner";

const FLAG_KEY = "prompt_workshop_name";

export default function WorkshopNamePrompt() {
  const { workshop, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!workshop) return;
    if (localStorage.getItem(FLAG_KEY) === "1") {
      setName(workshop.name || "");
      setOpen(true);
    }
  }, [workshop]);

  const skip = () => {
    localStorage.removeItem(FLAG_KEY);
    setOpen(false);
  };

  const save = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) { toast.error("Escribe un nombre para el taller"); return; }
    setSaving(true);
    try {
      await api.patch("/workshop/me", { name: trimmed });
      await refresh();
      localStorage.removeItem(FLAG_KEY);
      toast.success("Nombre del taller guardado");
      setOpen(false);
    } catch (err) {
      toast.error(errMsg(err, "No se pudo guardar el nombre"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) skip(); }}>
      <DialogContent data-testid="workshop-name-prompt">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-2xl">
            <Building2 className="w-6 h-6 text-blue-700" />
            ¿Cómo se llama tu taller?
          </DialogTitle>
          <DialogDescription>
            Le hemos puesto un nombre provisional a partir de tu cuenta de Google.
            Ponle el nombre real: aparecerá en la cabecera, en los PDF y en los correos que envíes a tus clientes.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={save} className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label htmlFor="workshop-name-prompt-input">Nombre del taller</Label>
            <Input
              id="workshop-name-prompt-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Taller Pérez"
              maxLength={100}
              autoFocus
              className="h-11"
              data-testid="workshop-name-prompt-input"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={skip}
              disabled={saving}
              data-testid="workshop-name-prompt-skip"
            >
              Ahora no
            </Button>
            <Button
              type="submit"
              disabled={saving || !name.trim()}
              className="bg-blue-700 hover:bg-blue-800"
              data-testid="workshop-name-prompt-save"
            >
              {saving ? "Guardando..." : "Guardar nombre"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
